import { useEffect, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import { LuCheck, LuRotateCcw } from 'react-icons/lu';
import { Select } from '../../ui/Form';
import { Button } from '../../ui/Button';
import { useStrings } from '../../tenant/TenantProvider';
import { useTheme } from '../../theme/ThemeProvider';
import { monacoLanguage } from '../../lib/languages';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MODIFIER = IS_MAC ? '⌘' : 'Ctrl';

function cssVar(name, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function defineWorkspaceTheme(monaco, theme) {
  const dark = theme === 'dark';
  const name = `workspace-${theme}`;
  monaco.editor.defineTheme(name, {
    base: dark ? 'vs-dark' : 'vs',
    inherit: true,
    rules: [
      { token: 'comment', foreground: cssVar('--syntax-comment', dark ? '#858c9f' : '#676d80').slice(1), fontStyle: 'italic' },
      { token: 'keyword', foreground: cssVar('--syntax-keyword', dark ? '#c792ff' : '#8a2be2').slice(1) },
      { token: 'string', foreground: cssVar('--syntax-string', dark ? '#5fe3a8' : '#0d7d55').slice(1) },
      { token: 'number', foreground: cssVar('--syntax-number', dark ? '#f5b84a' : '#8f5b00').slice(1) },
      { token: 'type', foreground: cssVar('--syntax-type', dark ? '#a9acff' : '#3f42d0').slice(1) },
    ],
    colors: {
      'editor.background': cssVar('--color-editor-bg', dark ? '#141518' : '#ffffff'),
      'editor.lineHighlightBackground': cssVar('--color-editor-line', dark ? '#1b1c20' : '#f7f7f8'),
      'editor.lineHighlightBorder': '#00000000',
      'editorLineNumber.foreground': cssVar('--color-editor-gutter', dark ? '#5e5f66' : '#a3a4ab'),
      'editorLineNumber.activeForeground': cssVar('--color-editor-gutter-active', dark ? '#ececee' : '#111113'),
      'editorCursor.foreground': cssVar('--color-editor-cursor', dark ? '#8195ff' : '#3b57d6'),
      'editor.selectionBackground': cssVar('--color-editor-selection', dark ? '#2b3768' : '#d6ddfb'),
      'editorIndentGuide.background1': cssVar('--color-border', dark ? '#26272c' : '#e6e6e9'),
      'editorWidget.background': cssVar('--color-surface-raised', dark ? '#1b1c20' : '#ffffff'),
      'editorWidget.border': cssVar('--color-border', dark ? '#26272c' : '#e6e6e9'),
      'scrollbarSlider.background': `${cssVar('--color-border-strong', dark ? '#37383e' : '#d2d2d7')}aa`,
    },
  });
  return name;
}

export function CodeEditor({ code, onChange, language, languages, onLanguageChange, onReset, onRun, onSubmit, readOnly, savedAt }) {
  const t = useStrings();
  const { theme } = useTheme();
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const surfaceRef = useRef(null);
  const handlersRef = useRef({ onRun, onSubmit });
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    handlersRef.current = { onRun, onSubmit };
  }, [onRun, onSubmit]);

  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco) monaco.editor.setTheme(defineWorkspaceTheme(monaco, theme));
  }, [theme]);

  useEffect(() => {
    if (!savedAt) return undefined;
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), 1800);
    return () => clearTimeout(timer);
  }, [savedAt]);

  useEffect(() => {
    if (!confirmingReset) return undefined;
    const timer = setTimeout(() => setConfirmingReset(false), 5000);
    return () => clearTimeout(timer);
  }, [confirmingReset]);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return undefined;
    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => editorRef.current?.layout({ width: Math.floor(width), height: Math.floor(height) }));
    });
    observer.observe(surface);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  const handleMount = (editor, monaco) => {
    monacoRef.current = monaco;
    editorRef.current = editor;
    const surface = surfaceRef.current;
    if (surface) editor.layout({ width: surface.clientWidth, height: surface.clientHeight });
    monaco.editor.setTheme(defineWorkspaceTheme(monaco, document.documentElement.getAttribute('data-theme') || 'light'));
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => handlersRef.current.onRun?.());
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Enter, () => handlersRef.current.onSubmit?.());
  };

  return (
    <section className="pane code-editor">
      <div className="pane__toolbar">
        <Select
          label={t('workspace.editor.language')}
          value={language}
          onChange={onLanguageChange}
          options={languages.map((value) => ({ value, label: t(`languages.${value}`, { fallback: value }) }))}
          className="code-editor__language"
        />
        <span className={`code-editor__saved${showSaved ? ' is-visible' : ''}`} aria-live="polite">
          {showSaved && (
            <>
              <LuCheck aria-hidden="true" />
              {t('workspace.editor.saved')}
            </>
          )}
        </span>
        <div className="pane__toolbar-end">
          {confirmingReset ? (
            <span className="code-editor__confirm">
              <span>{t('workspace.editor.resetConfirm')}</span>
              <Button size="sm" variant="danger" onClick={() => { onReset(); setConfirmingReset(false); }}>
                {t('workspace.editor.reset')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingReset(false)}>
                {t('common.cancel')}
              </Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" icon={LuRotateCcw} disabled={readOnly} onClick={() => setConfirmingReset(true)}>
              {t('workspace.editor.reset')}
            </Button>
          )}
        </div>
      </div>
      <div ref={surfaceRef} className="code-editor__surface">
        <Editor
          height="100%"
          language={monacoLanguage(language)}
          value={code}
          onChange={(value) => onChange(value ?? '')}
          onMount={handleMount}
          loading={null}
          options={{
            readOnly,
            fontFamily: getComputedStyle(document.documentElement).getPropertyValue('--font-mono'),
            fontSize: 13.5,
            lineHeight: 21,
            fontLigatures: false,
            minimap: { enabled: false },
            padding: { top: 12, bottom: 12 },
            scrollBeyondLastLine: false,
            smoothScrolling: true,
            renderLineHighlight: 'line',
            tabSize: 4,
            automaticLayout: false,
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            guides: { indentation: true },
            stickyScroll: { enabled: false },
          }}
        />
      </div>
    </section>
  );
}
